import { differenceInCalendarDays } from 'date-fns'
import { Decimal } from 'decimal.js'

import type { IParcela } from '@/features/simulador/shared/types/calculadora-parcela.types'

export class CalculadoraDeParcelasBase {
  calcularValorDeImpostoDasParcelas(
    parcelas: IParcela[],
    percentualImpostos: number,
    percentualISS: number,
    valorParcelaBase: 'valor' | 'valorSemCorrecao',
  ): IParcela[] {
    const taxaImposto = percentualImpostos / 100
    const taxaISS = percentualISS / 100

    return parcelas.map((parcela) => ({
      ...parcela,
      imposto: parcela[valorParcelaBase]! * (taxaImposto + taxaISS),
    }))
  }

  calcularValorDePontoMedioDasParcelas(
    parcelas: IParcela[],
    taxaJurosDiarioAntesPontoMedio: number,
    taxaJurosDiarioDepoisPontoMedio: number,
    dataDoPontoMedio: string,
  ) {
    const parcelaSaldo = parcelas.find((parcela) => parcela.tipo === 'saldo')

    const converteParaValorPontoMedio = (parcela: IParcela): number => {
      const dataParcela = parcela.tipo === 'posConclusao' && parcelaSaldo?.data ? parcelaSaldo.data : parcela.data
      const quantidadeDias = differenceInCalendarDays(new Date(dataDoPontoMedio), new Date(dataParcela))
      const taxaDiaria = quantidadeDias > 0 ? taxaJurosDiarioAntesPontoMedio : taxaJurosDiarioDepoisPontoMedio
      const valorPontoMedio = (parcela.valorSemImpostoEComissao ?? 0) * (1 + taxaDiaria) ** quantidadeDias
      return valorPontoMedio
    }

    return parcelas.map((parcela) => ({
      ...parcela,
      valorPontoMedio: converteParaValorPontoMedio(parcela),
    }))
  }

  calcularValorSemImpostoEComissaoDasParcelas(
    parcelas: IParcela[],
    comissaoVendedor: number,
    valorParcelaBase: 'valor' | 'valorSemCorrecao',
  ): IParcela[] {
    let commissaoVendedorRestante = comissaoVendedor

    return parcelas.map((parcela, index) => {
      const isLast = index === parcelas.length - 1
      const valorSemImposto = parcela[valorParcelaBase]! - (parcela.imposto ?? 0)

      let valorSemImpostoEComissao = valorSemImposto - commissaoVendedorRestante

      if (valorSemImpostoEComissao < 0) {
        if (isLast) {
          commissaoVendedorRestante = 0
        } else {
          commissaoVendedorRestante -= Math.max(0, valorSemImposto)
          valorSemImpostoEComissao = 0
        }
      } else {
        commissaoVendedorRestante = 0
      }

      return { ...parcela, valorSemImpostoEComissao }
    })
  }

  calcularValorSemCorrecaoDasParcelas = (
    parcelas: IParcela[],
    dataSimulacao: string,
    taxaDiariaINCC: number,
  ): IParcela[] => {
    const dataSimulacaoFormatada = new Date(dataSimulacao)

    return parcelas.map((parcela) => {
      const quantidadeDias = differenceInCalendarDays(new Date(parcela.data), dataSimulacaoFormatada)
      const valorSemCorrecao = parcela.valor / (1 + taxaDiariaINCC) ** quantidadeDias

      return { ...parcela, valorSemCorrecao }
    })
  }

  somar(parcelas: IParcela[], field: Extract<keyof IParcela, 'valorPontoMedio' | 'valorPresente'>) {
    return parcelas.reduce((soma, valor) => soma.plus(valor[field] ?? 0), new Decimal(0)).toNumber()
  }
}
